/**
 * File Validation Utilities
 * Client-side validation for file uploads
 */

// Blocked extensions (security risk)
const BLOCKED_EXTENSIONS = [
  '.exe',
  '.bat',
  '.sh',
  '.cmd',
  '.scr',
  '.com',
  '.pif',
  '.app',
  '.deb',
  '.rpm',
  '.msi',
  '.vbs',
  '.js', // Block standalone JS files
  '.jar',
];

// Max file size: 500MB
const MAX_FILE_SIZE = 500 * 1024 * 1024;

// Max voice message size: 10MB
const MAX_VOICE_MESSAGE_SIZE = 10 * 1024 * 1024;

/**
 * Validates file type against blocked extensions
 */
export function validateFileType(file: File): boolean {
  const extension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] || '';
  return !BLOCKED_EXTENSIONS.includes(extension);
}

/**
 * Validates file size (max 500MB)
 */
export function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_SIZE;
}

/**
 * Gets validation error message in Bulgarian
 * Returns null if file is valid
 */
export function getValidationError(file: File): string | null {
  if (!validateFileType(file)) {
    return 'Този тип файл не е разрешен';
  }

  // Special validation for voice messages
  if (file.type.startsWith('audio/') && file.name.startsWith('voice-message-')) {
    if (file.size > MAX_VOICE_MESSAGE_SIZE) {
      const sizeMB = Math.round(file.size / 1024 / 1024);
      return `Гласовото съобщение е твърде дълго (${sizeMB}MB, макс. 10MB)`;
    }
  } else if (!validateFileSize(file)) {
    const sizeMB = Math.round(file.size / 1024 / 1024);
    return `Файлът е твърде голям (${sizeMB}MB, макс. 500MB)`;
  }

  return null;
}

/**
 * Categorizes file by MIME type
 */
export function getFileCategory(
  file: File
): 'image' | 'video' | 'audio' | 'document' {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'document';
}

/**
 * Formats file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Gets emoji icon for file based on extension
 */
export function getFileIcon(fileName: string): string {
  const ext = fileName.toLowerCase().match(/\.[^.]+$/)?.[0] || '';

  // Document icons mapping
  const iconMap: Record<string, string> = {
    // Documents
    '.pdf': '📄',
    '.doc': '📘',
    '.docx': '📘',
    '.xls': '📊',
    '.xlsx': '📊',
    '.ppt': '📙',
    '.pptx': '📙',
    '.txt': '📝',
    '.md': '📝',
    '.csv': '📊',

    // Archives
    '.zip': '📦',
    '.rar': '📦',
    '.7z': '📦',
    '.tar': '📦',
    '.gz': '📦',

    // Audio
    '.mp3': '🎵',
    '.wav': '🎵',
    '.flac': '🎵',
    '.m4a': '🎵',
    '.ogg': '🎵',
    '.webm': '🎵', // Voice messages (Chrome/Edge)

    // Video
    '.mp4': '🎬',
    '.avi': '🎬',
    '.mkv': '🎬',
    '.mov': '🎬',
    '.wmv': '🎬',

    // Images
    '.jpg': '🖼️',
    '.jpeg': '🖼️',
    '.png': '🖼️',
    '.gif': '🖼️',
    '.webp': '🖼️',
    '.avif': '🖼️',
    '.svg': '🖼️',
    '.bmp': '🖼️',

    // Code
    '.html': '💻',
    '.css': '💻',
    '.ts': '💻',
    '.tsx': '💻',
    '.json': '💻',
    '.xml': '💻',
    '.yaml': '💻',
    '.yml': '💻',
  };

  return iconMap[ext] || '📎';
}

/**
 * Validates multiple files and returns valid/invalid lists
 */
export function validateFiles(files: File[]): {
  valid: File[];
  invalid: { file: File; error: string }[];
} {
  const valid: File[] = [];
  const invalid: { file: File; error: string }[] = [];

  for (const file of files) {
    const error = getValidationError(file);
    if (error) {
      invalid.push({ file, error });
    } else {
      valid.push(file);
    }
  }

  return { valid, invalid };
}
